import { Nullable } from "sonobello.utilities.react";

/** A binding of an event to some custom data we want to associate with it.
 * @typeparam TCustom - The type of custom data we want to bind to the event.
 */
export interface IEventSource<TCustom = undefined> {
  /** The identifier for this event source to uniquely distinguish it from events associated with other sources. */
  readonly key: string;
  /** Some static custom data we want to bind to the topic. */
  readonly custom: TCustom;
}

/** See {@link IEventSource}. */
export class EventSource<TCustom = undefined> implements IEventSource<TCustom> {
  readonly key: string;
  readonly custom: TCustom;

  constructor(key: string, custom: TCustom) {
    this.key = key;
    this.custom = custom;
  }

  /** Bind an event body to its topic source.
   * @param body - The event body that will be bound to the event source properties.
   * @typeParam TPayload - The type of the event body that will be bound to the event.
   */
  GetEvent<TPayload>(body: TPayload): Event<TCustom, TPayload> {
    return new Event(this.key, this.custom, body);
  }
}

/** A binding of an event body to its source.
 * @typeparam TCustom - The type of custom data we want to bind to the event.
 * @typeparam TPayload - The type of the event body.
 */
export interface IEvent<TCustom, TPayload> extends IEventSource<TCustom> {
  /** The content body for the event. */
  readonly body: Nullable<TPayload>;
}

/** See {@link IEvent}. */
export class Event<TCustom, TPayload> extends EventSource<TCustom> implements IEvent<TCustom, TPayload> {
  body: Nullable<TPayload>;

  /**
   * @param key - The identifier for this event source to uniquely distinguish it from events associated with other sources.
   * @param custom - Some static custom data we want to bind to the topic.
   * @param body - The event body that will be bound to the event source properties.
   */
  constructor(key: string, custom: TCustom, body: Nullable<TPayload>) {
    super(key, custom);
    this.body = body;
  }
}
